import React, { RefObject, useRef } from "react";
import { useAppSelector } from "../../hooks";
import Gauge from "../layout/gauges/Gauge";
import { DownloadFinished, DownloadPending, DownloadRunning } from "./gaugeInfoBlocks/download/downloadState";
import { UploadFinished, UploadPending, UploadRunning } from "./gaugeInfoBlocks/upload/uploadState";
import { PingFinished, PingPending, PingRunning } from "./gaugeInfoBlocks/ping/pingState";
import { formatPingTime } from "../../helper/unitsFormatting"
import { AppAssertionError } from "../../features/errors/errorSlice";
import InfoWidget from "../elements/InfoWidget";
import ModalDialog from "../elements/ModalDialog";
import * as R from "ramda";
import { isRunningInBrowser } from "../../helper/ssr";
import { logger } from "../../helper/logger";
import Speechmark from "../elements/spinner/Speechmark";
import AnimatedText from "../elements/spinner/AnimatedText";
import ButtonContainer from "./ButtonContainer";
import t from "../../helper/t";
import SmartLink from "../elements/SmartLink";
import { formatSpeed } from "../../helper/formatSpeeds";
import { getLastItem } from "../../helper/utils";
import { Phase } from "../../features/measurement/types";
import { isDownloadFinished, isPingFinished, isUploadFinished, selectIsCustomer, selectPrepareResult } from "../../features/measurement/utils";

import imageDownload from "../../../public/img/measurement/download.svg";
import imagePingPing from "../../../public/img/measurement/ping-ping.svg";
import imageUpload from "../../../public/img/measurement/upload.svg";

interface DownUpGaugeProps {
  id: string;
  speed: number | null;
  maxSpeed: number;
}
const DownUpGauge: React.FC<DownUpGaugeProps> = ({ id, speed, maxSpeed }) => {
  const { unit, values: [speedStrOrNull] } = formatSpeed([speed]);

  return (
    <>
      <Gauge speed_bps={speed} maxSpeed_bps={maxSpeed} />
      <p className="speedtest-gauge-label"
        data-content={speedStrOrNull === null ? "" : unit}
        id={id}>
        {speedStrOrNull === null ? "--" : speedStrOrNull}
      </p>
    </>
  )
}

interface DownUpPingProps { }

const DownUpPing: React.FC<DownUpPingProps> = () => {
  const state = useAppSelector(state => state.measurement);
  const isCustomer = selectIsCustomer(state) ?? false;
  const prepareResult = selectPrepareResult(state);

  if (state.kind != Phase.Benchmarking &&
    state.kind != Phase.Downloading &&
    state.kind != Phase.Uploading &&
    state.kind != Phase.Pinging &&
    state.kind != Phase.Completing &&
    state.kind != Phase.ModemResolving &&
    state.kind != Phase.CompletingNoModem &&
    state.kind != Phase.FinishedNoModem) {
    throw new AppAssertionError({ actual: state.kind, expected: "one of Benchmarking Downloading Uploading Pinging ModemResolving CompletingNoModem FinishedNoModem" })
  }

  function title(): string {
    switch (state.kind) {
      case Phase.FinishedNoModem:
        return t("Hier sind Deine Messergebnisse");
      case Phase.NotStarted:
        return "";
      default:
        return t("Messung läuft");
    }
  }

  const maxDownloadSpeed: number = prepareResult?.init.modem?.bookedDownloadSpeedMax ?? 100e6;
  const maxUploadSpeed: number = prepareResult?.init.modem?.bookedUploadSpeedMax ?? 100e6;
  const maxPingTime = 100e-3;

  const getDownloadSpeed = (): number | null => {
    switch (state.kind) {
      case Phase.Benchmarking:
        return null;
      case Phase.Downloading:
        return getLastItem(state.downloadIntermediates);
      default:
        return state.download.speed;
    }
  }

  const getUploadSpeed = (): number | null => {
    switch (state.kind) {
      case Phase.Benchmarking:
      case Phase.Downloading:
        return null;
      case Phase.Uploading:
        return getLastItem(state.uploadIntermediates);
      default:
        return state.upload.speed;
    }
  }

  const getPingTime = (): number | null => {
    switch (state.kind) {
      case Phase.Benchmarking:
      case Phase.Downloading:
      case Phase.Uploading:
        return null;
      case Phase.Pinging:
        return getLastItem(state.pingIntermediates);
      default:
        return state.ping.pingTime;
    }
  }

  const downloadSpeed = getDownloadSpeed();
  const uploadSpeed = getUploadSpeed();
  const pingTime = getPingTime();

  function formatTimeNull(time: number | null): string {
    if (time) {
      return formatPingTime(time)
    } else {
      return "--"
    }
  }

  const downloadCardRef = useRef<HTMLDivElement>(null);
  const uploadCardRef = useRef<HTMLDivElement>(null);
  const pingCardRef = useRef<HTMLDivElement>(null);
  const mobileMarkerRef = useRef<HTMLDivElement>(null);

  const scrollOnMobile = (ref?: RefObject<HTMLDivElement>) => {
    if (R.isNil(mobileMarkerRef.current) || !isRunningInBrowser()) {
      return;
    }

    const style = window.getComputedStyle(mobileMarkerRef.current);
    const display = style.display;
    if (display === "none") {
      if (R.isNil(ref)) {
        scrollTo(0, 0);
      } else {
        ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }

  if (state.kind === Phase.Downloading && downloadSpeed === null) {
    scrollOnMobile(downloadCardRef);
  } else if (state.kind === Phase.Uploading && uploadSpeed === null) {
    scrollOnMobile(uploadCardRef);
  } else if (state.kind === Phase.Pinging && pingTime === null) {
    scrollOnMobile(pingCardRef);
  } else if (state.kind === Phase.FinishedNoModem) {
    logger.info("FinishedNoModem");
    scrollOnMobile();
  }

  return (
    <>
      <div className="speedtest-elements-wrapper">
        <div ref={mobileMarkerRef} className="nomobile">
        </div>
        <span className="speedtest-headline">
          <p>{title()}</p>
        </span>
        <div className="speedtest-elements-container">
          {/* Download */}
          <div ref={downloadCardRef} data-cy="card-download" className={`speedtest-card-item card-download ${state.kind === Phase.Downloading ? "zoom-in-from-left" : ""} ${isDownloadFinished(state.kind) ? "zoom-out" : ""}`}>
            <div className="speedtest-card-title">
              <div className="speedtest-card-title-wrapper">
                <div className="speedtest-card-title-icon">
                  {state.kind === Phase.Downloading && (
                    <img src={imageDownload.src} alt="download" />
                  )}
                </div>
                <label className="speedtest-card-title-label">{t("Download")}</label>
              </div>
            </div>
            <div className={`speedtest-gauge-container ${(state.kind === Phase.Downloading || isDownloadFinished(state.kind)) ? "" : "speedtest-gauge-container-inactive"}`}>
              <DownUpGauge id="downloadMeasurementValue" speed={downloadSpeed} maxSpeed={maxDownloadSpeed} />
            </div>
            {state.kind === Phase.Benchmarking && <DownloadPending />}
            {state.kind === Phase.Downloading && <DownloadRunning />}
            {(state.kind === Phase.Uploading ||
              state.kind === Phase.Pinging ||
              state.kind === Phase.Completing ||
              state.kind === Phase.ModemResolving ||
              state.kind === Phase.CompletingNoModem ||
              state.kind === Phase.FinishedNoModem) &&
              <DownloadFinished actualSpeed_bps={state.download.speed} bookedSpeed_bps={state.prepareResult.init.modem?.bookedDownloadSpeedMax} />
            }
          </div>





          {/* Upload */}
          <div ref={uploadCardRef} data-cy="card-upload" className={`speedtest-card-item card-upload ${state.kind == Phase.Uploading ? "zoom-in" : ""} ${isUploadFinished(state.kind) ? "zoom-out" : ""}`}>
            <div className="speedtest-card-title">
              <div className="speedtest-card-title-wrapper">
                <div className="speedtest-card-title-icon">
                  {state.kind == Phase.Uploading && (
                    <img src={imageUpload.src} alt="upload" />
                  )}
                </div>
                <label className="speedtest-card-title-label">{t("Upload")}</label>
              </div>
            </div>
            <div className={`speedtest-gauge-container ${state.kind === Phase.Uploading || isUploadFinished(state.kind) ? "" : "speedtest-gauge-container-inactive"}`}>
              <DownUpGauge id="uploadMeasurementValue" speed={uploadSpeed} maxSpeed={maxUploadSpeed} />
            </div>
            {/* Texte */}
            {(state.kind === Phase.Benchmarking || state.kind === Phase.Downloading) && <UploadPending />}
            {state.kind === Phase.Uploading && <UploadRunning />}
            {(state.kind === Phase.Pinging ||
              state.kind === Phase.Completing ||
              state.kind === Phase.ModemResolving ||
              state.kind === Phase.CompletingNoModem ||
              state.kind === Phase.FinishedNoModem) &&
              (<UploadFinished actualSpeed_bps={state.upload.speed} bookedSpeed_bps={state.prepareResult.init.modem?.bookedUploadSpeedMax} />)
            }
          </div>





          {/* Ping */}
          <div ref={pingCardRef} data-cy="card-ping" className={`speedtest-card-item card-ping ${state.kind == Phase.Pinging ? "zoom-in-from-right" : ""} ${isPingFinished(state.kind) ? "zoom-out" : ""}`}>
            <div className="speedtest-card-title">
              <div className="speedtest-card-title-wrapper">
                <div className="speedtest-card-title-icon">
                  {state.kind == Phase.Pinging && (
                    <img src={imagePingPing.src} alt="ping" />
                  )}
                </div>
                <label className="speedtest-card-title-label">{t("Ping")}</label>
              </div>
            </div>
            <div className={`speedtest-gauge-container ${state.kind === Phase.Pinging || isPingFinished(state.kind) ? "" : "speedtest-gauge-container-inactive"}`}>
              <Gauge speed_bps={pingTime} maxSpeed_bps={maxPingTime} />
              <p
                className="speedtest-gauge-label"
                data-content={
                  typeof pingTime === "number" ? t("ms") : ""
                }
                id="pingMeasurementValue"
              >
                {formatTimeNull(pingTime)}
              </p>
            </div>
            {(state.kind === Phase.Benchmarking ||
              state.kind === Phase.Downloading ||
              state.kind === Phase.Uploading) &&
              <PingPending />
            }
            {state.kind === Phase.Pinging && <PingRunning />}
            {(state.kind === Phase.Completing ||
              state.kind === Phase.ModemResolving ||
              state.kind === Phase.CompletingNoModem ||
              state.kind === Phase.FinishedNoModem) &&
              <PingFinished actualPingTime={state.ping.pingTime} isCustomer={isCustomer} />
            }
          </div>

        </div>
        {(state.kind === Phase.ModemResolving || state.kind === Phase.CompletingNoModem) && <ModemResolving />}
        {state.kind == Phase.FinishedNoModem && <ResultButtons />}
        <InfoWidget prepareResult={state.prepareResult} />
      </div>
      {state.kind === Phase.Benchmarking && <Benchmarking />}
    </>
  );

}

export default DownUpPing;

const ResultButtons: React.FC<{}> = () => {
  return (
    <ButtonContainer>
      <SmartLink href="/tipps">
        <div className="button-rectangle-left rectangle-grey">
          <div data-cy="button-tips" className="button-rectangle-text white-label">
            {t("Tipps zur Optimierung")}
          </div>
        </div>
      </SmartLink>
      <SmartLink href="/report">
        <div className="button-rectangle-right">
          <div data-cy="previous-test-results" className="button-rectangle-text">
            {t("Bisherige Testergebnisse")}
          </div>

        </div>
      </SmartLink>
    </ButtonContainer>);
}

const Benchmarking: React.FC<{}> = () => {
  return (
    <ModalDialog fullScreenInMobile={true}>
      <div style={{ height: 150, marginTop: 10 }}>
        <Speechmark />
      </div>
      <div style={{ marginTop: 24 }}>
        <AnimatedText messages={[t("Willkommen! Wir ermitteln die Leistung Deines Endgerätes.")]} />
      </div>
    </ModalDialog>
  )
};

const ModemResolving: React.FC<{}> = () => {
  return (
    <ModalDialog fullScreenInMobile={true}>
      <div style={{ height: 150, marginTop: 36 }}>
        <Speechmark />
      </div>
      <div style={{ marginTop: 24, minHeight: 52 }}>
        <AnimatedText messages={[t("Deine Daten laden noch."), t("Fast fertig."), t("Es dauert noch ein bisschen.")]} />
      </div>
    </ModalDialog>
  );
}